import React, { useState, useEffect } from 'react';
import { client, urlFor } from '../../client'; // Adjust path if needed
import { PortableText } from '@portabletext/react';

// Placeholder for loading state
const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#2a4865]"></div> 
    <p className="ml-4 text-lg text-gray-700">Loading...</p>
  </div>
);

// Placeholder for error state
const ErrorMessage = ({ message }) => (
  <div className="container mx-auto px-4 py-12 text-center">
    <p className="text-red-600 font-semibold">{message || 'Error loading page content. Please try again later.'}</p>
  </div>
);


const FourthGrade = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('welcome');

  useEffect(() => {
    const query = `*[_type == "fourthGradePage" && _id == "fourthGradePageContent"][0]{
      pageTitle,
      bannerTitle,
      bannerImage {
        asset->{
          _id,
          url
        }
      },
      contentSections[]{
        _key,
        title,
        body
      }
    }`;

    setLoading(true);
    client.fetch(query)
      .then((data) => {
        if (data) {
          setPageData(data);
        } else {
          setError('Fourth Grade Page content not found.');
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch fourth grade page data:', err);
        setError('Failed to load page content. Please try again later.');
        setLoading(false);
      });
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!pageData) return <ErrorMessage message="No content available for this page." />;

  return (
    <div className="bg-white">
      {/* Hero Banner with Image Overlay */}
      <section className="relative h-[40vh] max-h-[400px]">
        <div className="w-full h-full relative overflow-hidden">
          {pageData.bannerImage?.asset?.url ? (
            <img 
              src={urlFor(pageData.bannerImage).url()} 
              alt={pageData.bannerTitle || 'PS52 Fourth Grade Banner'} 
              className="absolute inset-0 w-full h-full object-cover object-center" 
              loading="lazy"
            />
          ) : (
             <div className="absolute inset-0 w-full h-full bg-gray-300"></div> // Placeholder background
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20"></div>
        </div>
        
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-6 md:px-12 lg:px-16">
            <div className="max-w-3xl pb-12 md:pb-16 lg:pb-16 text-left">
              <div className="w-16 h-1 bg-yellow-500 mb-6"></div>
              
              <h1 className="text-white text-4xl md:text-5xl font-bold mb-2 leading-tight text-left">
                {pageData.bannerTitle || 'Fourth Grade'}
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-6xl mx-auto">
            
            {/* Tabs */}
            <div className="flex overflow-x-auto pb-2 mb-8">
              <button
                className={`py-3 px-6 font-bold rounded-full mr-4 transition-colors whitespace-nowrap ${
                  activeTab === 'welcome'
                    ? 'bg-[#691e31] text-white shadow-md'
                    : 'bg-[#2a4865]/10 text-[#2a4865] hover:bg-[#2a4865]/20'
                }`}
                onClick={() => setActiveTab('welcome')}
              >
                Welcome to Fourth Grade
              </button>
              <button
                className={`py-3 px-6 font-bold rounded-full mr-4 transition-colors whitespace-nowrap ${
                  activeTab === 'newsletter'
                    ? 'bg-[#691e31] text-white shadow-md'
                    : 'bg-[#2a4865]/10 text-[#2a4865] hover:bg-[#2a4865]/20'
                }`}
                onClick={() => setActiveTab('newsletter')}
              >
                Monthly Newsletter
              </button>
            </div>

            {/* Welcome Tab Content */}
            {activeTab === 'welcome' && (
              <div className="space-y-6">
                {pageData.contentSections && pageData.contentSections.length > 0 ? (
                  pageData.contentSections.map((section) => (
                    <section key={section._key} className="bg-[#2a4865]/5 p-5 rounded-lg border-l-4 border-[#2a4865]">
                      <h3 className="text-xl font-bold text-[#2a4865] mb-3 relative after:content-[''] after:absolute after:w-10 after:h-0.5 after:bg-yellow-500 after:-bottom-1 after:left-0">
                        {section.title}
                      </h3>
                      <div className="prose prose-sm sm:prose max-w-none text-gray-700">
                        <PortableText value={section.body} />
                      </div>
                    </section>
                  ))
                ) : (
                  <p>No content sections available.</p>
                )}
              </div>
            )}

            {/* Newsletter Tab Content */}
            {activeTab === 'newsletter' && (
              <div className="space-y-6">
                <section className="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="bg-[#2a4865] text-white p-6 rounded-t-lg">
                    <h3 className="text-2xl font-bold mb-2 text-white">January 2025 Newsletter</h3>
                    <p className="text-white/80 text-sm">Fourth Grade Class • Mrs. Chen</p>
                  </div>
                  
                  <div className="p-6 prose prose-lg max-w-none">
                    <h2 className="text-xl font-bold text-[#691e31] mb-4">Welcome Back, Fourth Grade Families! 🌟</h2>
                    
                    <p className="text-gray-700 mb-6">
                      Happy New Year! We're ready to tackle new challenges and continue growing as learners. 
                      Fourth grade is such an exciting time as students become more independent and confident!
                    </p>

                    <h2 className="text-xl font-bold text-[#691e31] mb-4">What We're Learning This Month 📚</h2>
                    
                    <p className="text-gray-700 mb-3">
                      <strong>Language Arts:</strong> We're exploring narrative writing and character development. 
                      Students are reading chapter books independently and practicing summarizing skills.
                    </p>
                    
                    <p className="text-gray-700 mb-3">
                      <strong>Math:</strong> We're working with multi-digit multiplication and division, 
                      exploring factors and multiples, and beginning our unit on fractions.
                    </p>
                    
                    <p className="text-gray-700 mb-6">
                      <strong>Science & Social Studies:</strong> Our focus is on ecosystems and habitats, 
                      plus learning about different regions of the United States.
                    </p>

                    <h2 className="text-xl font-bold text-[#691e31] mb-4">Important Dates 📅</h2>
                    
                    <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                      <li><strong>January 15:</strong> MLK Day - No School</li>
                      <li><strong>January 18:</strong> Ecosystem Diorama Projects Due</li>
                      <li><strong>January 25:</strong> Math Multiplication Assessment</li>
                      <li><strong>February 1:</strong> Author Visit - Patricia Polacco (Virtual)</li>
                    </ul>

                    <h2 className="text-xl font-bold text-[#691e31] mb-4">Building Independence 🦅</h2>
                    
                    <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                      <li>Encourage daily reading for 30 minutes</li>
                      <li>Practice multiplication facts to build fluency</li>
                      <li>Help with organization but let them pack their own backpack</li>
                      <li>Discuss current events and encourage questions</li>
                    </ul>

                    <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                      <h3 className="font-bold text-[#691e31] mb-2">📧 Stay Connected!</h3>
                      <p className="text-gray-700 text-sm">
                        Please feel free to reach out with any questions or concerns. 
                        I love hearing about what your child is learning and discovering at home!
                      </p>
                      <p className="text-gray-700 text-sm mt-2">
                        <strong>Here's to a fantastic second half of the year!</strong><br/>
                        <em>- Mrs. Chen, Fourth Grade Teacher</em>
                      </p>
                    </div>
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default FourthGrade; 